import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { NumberInput, SelectInput, useGetList } from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import { callApi } from "@/dataprovider/misc-apis";
import { formatINR } from "@/utils/app-formatters";
import {
  DEFAULT_FMB_OUTSTANDING_MIN_PERCENT,
  FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY,
  FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_ONLY,
} from "./utils";
import { usePreviewFmbOutstandingRecipients } from "./hooks";

type FmbOutstandingRecipientRow = {
  phone?: string;
  memberItsNo?: string;
  memberRole?: string;
  recipientName?: string;
  householdItsNo?: string;
  householdName?: string;
  takhmeenAmount?: number;
  paidBalance?: number;
  pendingBalance?: number;
  outstandingPercent?: number;
  fmbId?: string;
};

type PeriodChoice = { id: number; name: string };

type NamedChoice = { id: string; name: string };

/** Normalize freeSolo Autocomplete values (string | choice) to an ITS id. */
function itsChoiceToId(value: string | NamedChoice): string {
  if (typeof value === "string") {
    return value.trim();
  }
  return String(value.id ?? "").trim();
}

function itsChoiceLabel(value: string | NamedChoice): string {
  if (typeof value === "string") {
    return value;
  }
  return value.name;
}

type FmbOutstandingRecipientPanelProps = {
  onPreviewCount?: (count: number) => void;
  onPreviewed?: (previewed: boolean) => void;
  onSelectionChange?: (
    recipients: { ITS_ID: string | null; phone: string; Full_Name: string | null }[]
  ) => void;
};

export default function FmbOutstandingRecipientPanel({
  onPreviewCount,
  onPreviewed,
  onSelectionChange,
}: FmbOutstandingRecipientPanelProps) {
  const { control, setValue } = useFormContext();
  const hijriYearStart = useWatch({ control, name: "hijriYearStart" }) as number | undefined;
  const minOutstandingPercent = useWatch({ control, name: "minOutstandingPercent" }) as
    | number
    | undefined;
  const thaliTypeIds = useWatch({ control, name: "thaliTypeIds" }) as string[] | undefined;
  const deliveryScheduleProfileIds = useWatch({
    control,
    name: "deliveryScheduleProfileIds",
  }) as string[] | undefined;
  const itsIds = useWatch({ control, name: "itsIds" }) as string[] | undefined;
  const notifyMode = useWatch({ control, name: "notifyMode" }) as string | undefined;

  const [periodChoices, setPeriodChoices] = useState<PeriodChoice[]>([]);
  const [periodsLoading, setPeriodsLoading] = useState(true);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [itsSearchText, setItsSearchText] = useState("");

  const { data: thaliTypes = [], isLoading: thaliTypesLoading } = useGetList("fmbThaliType", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "code", order: "ASC" },
  });
  const { data: schedules = [], isLoading: schedulesLoading } = useGetList(
    "fmbDeliveryScheduleProfile",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "code", order: "ASC" },
    }
  );
  const trimmedItsSearch = itsSearchText.trim();
  const { data: itsSearchRows = [], isLoading: itsSearchLoading } = useGetList(
    "itsdata",
    {
      filter: trimmedItsSearch ? { q: trimmedItsSearch } : {},
      pagination: { page: 1, perPage: 25 },
      sort: { field: "Full_Name", order: "ASC" },
    },
    { enabled: trimmedItsSearch.length >= 2 }
  );

  const thaliTypeChoices = useMemo<NamedChoice[]>(
    () =>
      thaliTypes.map((r) => ({
        id: String(r.id),
        name: `${String(r.code ?? "")} — ${String(r.name ?? "")}`.replace(/^ — /, "").trim(),
      })),
    [thaliTypes]
  );
  const scheduleChoices = useMemo<NamedChoice[]>(
    () =>
      schedules.map((r) => ({
        id: String(r.id),
        name: `${String(r.code ?? "")} — ${String(r.name ?? "")}`.replace(/^ — /, "").trim(),
      })),
    [schedules]
  );

  const itsChoices = useMemo<NamedChoice[]>(() => {
    const byId = new Map<string, NamedChoice>();
    for (const row of itsSearchRows) {
      const its = String(row.ITS_ID ?? "").trim();
      if (!its) continue;
      const name = String(row.Full_Name ?? "").trim();
      byId.set(its, { id: its, name: name ? `${its} · ${name}` : its });
    }
    for (const its of itsIds ?? []) {
      const id = String(its ?? "").trim();
      if (!id || byId.has(id)) continue;
      byId.set(id, { id, name: id });
    }
    return [...byId.values()];
  }, [itsSearchRows, itsIds]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPeriodsLoading(true);
      try {
        const res = await callApi({
          location: "fmbReceipt",
          method: "GET",
          id: "dashboard/periods",
        });
        const periods =
          (res?.data as { periods?: { hijriYearStart: number; label: string }[] } | undefined)
            ?.periods ?? [];
        const next = periods.map((p) => ({ id: p.hijriYearStart, name: p.label }));
        if (!cancelled) {
          setPeriodChoices(next);
          if (next.length > 0 && (hijriYearStart == null || Number.isNaN(Number(hijriYearStart)))) {
            setValue("hijriYearStart", next[0].id, { shouldDirty: false });
          }
        }
      } catch {
        if (!cancelled) {
          setPeriodChoices([]);
        }
      } finally {
        if (!cancelled) {
          setPeriodsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Only on mount — do not re-default when user clears period
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue]);

  useEffect(() => {
    if (minOutstandingPercent == null) {
      setValue("minOutstandingPercent", DEFAULT_FMB_OUTSTANDING_MIN_PERCENT, {
        shouldDirty: false,
      });
    }
    if (!notifyMode) {
      setValue("notifyMode", FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY, { shouldDirty: false });
    }
  }, [minOutstandingPercent, notifyMode, setValue]);

  const selectionKey = useMemo(
    () =>
      JSON.stringify({
        hijriYearStart,
        minOutstandingPercent,
        thaliTypeIds,
        deliveryScheduleProfileIds,
        itsIds,
        notifyMode,
      }),
    [
      hijriYearStart,
      minOutstandingPercent,
      thaliTypeIds,
      deliveryScheduleProfileIds,
      itsIds,
      notifyMode,
    ]
  );

  const { data, isLoading, error, refetch } = usePreviewFmbOutstandingRecipients({
    hijriYearStart,
    minOutstandingPercent,
    thaliTypeIds,
    deliveryScheduleProfileIds,
    itsIds,
    notifyMode,
    limit: 10000,
    offset: 0,
    enabled: shouldFetch,
  });

  const rows = useMemo(
    () => (data?.data as FmbOutstandingRecipientRow[] | undefined) ?? [],
    [data]
  );
  const total = data?.total ?? 0;
  const resolvedHijriYear = data?.hijriYearStart;

  useEffect(() => {
    setShouldFetch(false);
    onPreviewed?.(false);
    onSelectionChange?.([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionKey]);

  useEffect(() => {
    if (error) {
      onPreviewed?.(false);
      onSelectionChange?.([]);
    } else if (shouldFetch && !isLoading) {
      onPreviewCount?.(total);
      onPreviewed?.(total > 0);

      const selectedRecipients = rows
        .map((row) => {
          const phone = String(row.phone ?? "").trim();
          if (!phone) return null;
          return {
            ...row,
            phone,
            ITS_ID: String(row.memberItsNo ?? row.householdItsNo ?? "").trim() || null,
            Full_Name: String(row.recipientName ?? row.householdName ?? "").trim() || null,
          };
        })
        .filter((row): row is NonNullable<typeof row> => row !== null);

      onSelectionChange?.(selectedRecipients);
    }
  }, [total, isLoading, error, shouldFetch, rows, onPreviewCount, onPreviewed, onSelectionChange]);

  const handlePreview = useCallback(() => {
    setShouldFetch(true);
    if (shouldFetch) {
      void refetch();
    }
  }, [shouldFetch, refetch]);

  const invalidatePreview = useCallback(() => {
    setShouldFetch(false);
    onPreviewed?.(false);
    onSelectionChange?.([]);
  }, [onPreviewed, onSelectionChange]);

  const selectedThaliTypes = useMemo(
    () => thaliTypeChoices.filter((c) => (thaliTypeIds ?? []).includes(c.id)),
    [thaliTypeChoices, thaliTypeIds]
  );
  const selectedSchedules = useMemo(
    () => scheduleChoices.filter((c) => (deliveryScheduleProfileIds ?? []).includes(c.id)),
    [scheduleChoices, deliveryScheduleProfileIds]
  );
  const selectedItsChoices = useMemo(
    () => itsChoices.filter((c) => (itsIds ?? []).includes(c.id)),
    [itsChoices, itsIds]
  );

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, page, rowsPerPage]);

  const notifyLabel =
    notifyMode === FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_ONLY
      ? "household contact only"
      : "household + family";

  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Reminds households with outstanding FMB takhmeen. Template body amounts are filled from each
        household ledger. By default, HOF and adult family members (age &gt; 10) with WhatsApp are
        notified; phones are deduped so a shared number gets one message.
      </Typography>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Audience filters
          </Typography>

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              mb: 2,
            }}
          >
            <SelectInput
              source="hijriYearStart"
              label="Hijri period"
              choices={periodChoices}
              optionText="name"
              optionValue="id"
              disabled={periodsLoading || periodChoices.length === 0}
              helperText={
                periodChoices.length === 0 && !periodsLoading
                  ? "No FMB periods found. Add takhmeen for a Hijri year first."
                  : "Defaults to the latest period if left unset on send."
              }
              fullWidth
            />
            <NumberInput
              source="minOutstandingPercent"
              label="Min outstanding %"
              min={0}
              max={100}
              step={1}
              helperText="Include households with pending balance and outstanding % at or above this value (default 1)."
              fullWidth
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Autocomplete
              multiple
              options={thaliTypeChoices}
              loading={thaliTypesLoading}
              value={selectedThaliTypes}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, value) => {
                setValue(
                  "thaliTypeIds",
                  value.map((v) => v.id),
                  { shouldDirty: true }
                );
                invalidatePreview();
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Thali type(s)"
                  helperText="Optional. Household must have at least one active thali of a selected type."
                />
              )}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Autocomplete
              multiple
              options={scheduleChoices}
              loading={schedulesLoading}
              value={selectedSchedules}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, value) => {
                setValue(
                  "deliveryScheduleProfileIds",
                  value.map((v) => v.id),
                  { shouldDirty: true }
                );
                invalidatePreview();
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Thali schedule(s)"
                  helperText="Optional. Household must have at least one active thali on a selected schedule."
                />
              )}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Autocomplete
              multiple
              freeSolo
              filterSelectedOptions
              options={itsChoices}
              loading={itsSearchLoading}
              value={selectedItsChoices}
              getOptionLabel={(o) => itsChoiceLabel(o as string | NamedChoice)}
              isOptionEqualToValue={(a, b) =>
                itsChoiceToId(a as string | NamedChoice) ===
                itsChoiceToId(b as string | NamedChoice)
              }
              onInputChange={(_, value) => setItsSearchText(value)}
              onChange={(_, value) => {
                const next = [
                  ...new Set(
                    (value as Array<string | NamedChoice>).map(itsChoiceToId).filter(Boolean)
                  ),
                ];
                setValue("itsIds", next, { shouldDirty: true });
                invalidatePreview();
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Household ITS"
                  helperText="Optional. Limit to these FMB household ITS numbers (search directory or type an ITS and press Enter). Leave empty for all matching filters."
                />
              )}
            />
          </Box>

          <FormControl component="fieldset" sx={{ mt: 1 }}>
            <FormLabel component="legend">Notify</FormLabel>
            <RadioGroup
              row
              value={notifyMode ?? FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY}
              onChange={(e) => {
                setValue("notifyMode", e.target.value, { shouldDirty: true });
                invalidatePreview();
              }}
            >
              <FormControlLabel
                value={FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_AND_FAMILY}
                control={<Radio />}
                label="Household + family (recommended)"
              />
              <FormControlLabel
                value={FMB_OUTSTANDING_NOTIFY_HOUSEHOLD_ONLY}
                control={<Radio />}
                label="Household contact only"
              />
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      <Button variant="contained" onClick={handlePreview} disabled={isLoading}>
        Preview recipients
      </Button>

      {shouldFetch && isLoading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading recipients…</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {(error as Error).message || "Failed to preview recipients"}
        </Alert>
      )}

      {shouldFetch && !isLoading && !error && (
        <>
          <Alert severity={total > 0 ? "success" : "info"} sx={{ mt: 2, py: 0.5 }}>
            <Typography variant="body2" component="span" fontWeight="bold">
              {total}
            </Typography>{" "}
            WhatsApp messages will be sent (one per unique phone, {notifyLabel})
            {resolvedHijriYear != null ? ` for Hijri period ${resolvedHijriYear}` : ""}
          </Alert>
          {rows.length > 0 && (
            <Card variant="outlined" sx={{ mt: 2 }}>
              <TableContainer sx={{ maxHeight: 500, overflowX: "auto" }}>
                <Table size="small" stickyHeader sx={{ minWidth: isCompact ? 300 : 700 }}>
                  <TableHead>
                    <TableRow>
                      {isCompact ? (
                        <TableCell>Details</TableCell>
                      ) : (
                        <>
                          <TableCell>Member</TableCell>
                          <TableCell>Member ITS</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>Household</TableCell>
                          <TableCell>HOF ITS</TableCell>
                        </>
                      )}
                      <TableCell align="right">Takhmeen</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell align="right">Pending</TableCell>
                      <TableCell align="right">Outstanding %</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginated.map((row, index) => (
                      <TableRow
                        key={`${String(row.fmbId ?? "")}-${String(row.memberItsNo ?? row.phone ?? "")}-${index}`}
                      >
                        {isCompact ? (
                          <TableCell>
                            <Stack spacing={0.25}>
                              <Typography variant="caption" display="block">
                                <strong>Member:</strong> {String(row.recipientName ?? "—")}
                              </Typography>
                              <Typography variant="caption" display="block">
                                <strong>Member ITS:</strong> {String(row.memberItsNo ?? "—")}
                              </Typography>
                              <Typography variant="caption" display="block">
                                <strong>Role:</strong> {String(row.memberRole ?? "—")}
                              </Typography>
                              <Typography variant="caption" display="block">
                                <strong>Phone:</strong> {String(row.phone ?? "—")}
                              </Typography>
                              <Typography variant="caption" display="block">
                                <strong>Household:</strong> {String(row.householdName ?? "—")}
                              </Typography>
                              <Typography variant="caption" display="block">
                                <strong>HOF ITS:</strong> {String(row.householdItsNo ?? "—")}
                              </Typography>
                            </Stack>
                          </TableCell>
                        ) : (
                          <>
                            <TableCell>{String(row.recipientName ?? "—")}</TableCell>
                            <TableCell>{String(row.memberItsNo ?? "—")}</TableCell>
                            <TableCell>{String(row.memberRole ?? "—")}</TableCell>
                            <TableCell>{String(row.phone ?? "—")}</TableCell>
                            <TableCell>{String(row.householdName ?? "—")}</TableCell>
                            <TableCell>{String(row.householdItsNo ?? "—")}</TableCell>
                          </>
                        )}
                        <TableCell align="right">
                          {formatINR(row.takhmeenAmount, { empty: "—" })}
                        </TableCell>
                        <TableCell align="right">
                          {formatINR(row.paidBalance, { empty: "—" })}
                        </TableCell>
                        <TableCell align="right">
                          {formatINR(row.pendingBalance, { empty: "—" })}
                        </TableCell>
                        <TableCell align="right">
                          {row.outstandingPercent != null
                            ? `${Number(row.outstandingPercent).toFixed(1)}%`
                            : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Card>
          )}
        </>
      )}
    </Box>
  );
}
